
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator"
import VueTreeselect, { LOAD_CHILDREN_OPTIONS, ASYNC_SEARCH } from "@riophae/vue-treeselect"
import { namespace, State } from "vuex-class"
import api from "@/services/api"
import { AreaNodeDto } from "@/types/areaNodeDto"
import { BaseState } from "@/store"
import { CurrentUser } from "@/types/user"
import TreeSelectNode from "@/types/libs/TreeSelectNode"
import { largeAreaSize } from "@/constants/area"
const Cases = namespace("cases")

@Component({
	components: {
		"vue-treeselect": VueTreeselect
	}
})
export default class AreaTreeSelect extends Vue {
	$refs!: {
		treeSelect: any,
		[key: string]: HTMLElement // The rows of the dropdown
	}

	@Prop() public value!: TreeSelectNode[] | string
	@Prop({ default: false }) public appendToBody!: boolean
	@Prop({ default: true }) public clearable!: boolean
	@Prop({ default: false }) public multiple!: boolean
	@Prop({ default: false }) public readonly!: boolean
	@Prop({ type: Function, default: (option: TreeSelectNode) => option }) public reduce!: (
		areaNode: AreaNodeDto
	) => string
	@Prop({ default: "Select an area" }) public placeholder!: string
	@Prop({ default: false }) public fullPathValue!: boolean
	@Prop({ default: false }) public showState!: boolean
	@Prop() public areasToOmit!: any[]
	@Prop({ type: Boolean, default: false }) public clearInputOnSelect!: boolean
	@State((state: BaseState) => state.me) me!: CurrentUser

	@Cases.Action fetchAreaDictionary!: () => Promise<void>
	@Cases.State areaDictionary!: Map<string, string>

	// The currently loaded AreaNodes (Note: these values are not what is shown when searching.)
	private areaTree: AreaNodeDto[] = []
	private isSearching: boolean = false
	private isSearchComplete: boolean = true

	// True if the user exists within a tenant that has over 500 groups.
	// Forces the user to search for an area before showing results.
	private isAsyncSearchEnabled: boolean = false
	private isLoadingAreas: boolean = false
	private readonly fullPath: string = "ALL_WITH_INDETERMINATE"
	private readonly groupOnly: string = "BRANCH_PRIORITY"
	private readonly largeTenantSizeValue: number = largeAreaSize
	private readonly rowPrefix: string = "row_"
	private readonly minSearchQueryLength: number = 3

	// VueTreeSelect provides a retry button when an error occurs. We can hide that button using this field
	private hideRetryButton: boolean = false

	// This is required due to VueTreeselect not re-rendering correctly
	// See https://github.com/riophae/vue-treeselect/issues/483
	private rerenderKey: number = 0

	private async mounted (): Promise<void> {
		try {
			if (this.areaDictionary.size === 0) {
				await this.fetchAreaDictionary()
			}
			await this.loadAreaTree()
		} catch (err) {
			console.error("Unexpected exception on initial load: ", err)
		}
	}

	// Loads the area tree based on the number of groups in the current users tenant.
	@Watch("me.tenantSize")
	private async loadAreaTree (): Promise<void> {
		this.isLoadingAreas = true
		if (this.me && this.me.tenantSize && this.me.tenantSize > this.largeTenantSizeValue) {
			this.isAsyncSearchEnabled = true
		} else {
			this.areaTree = this.setLeafNodes(await api.getFullAreaTree())
		}
		this.isLoadingAreas = false
	}

	private get filteredAreas ():AreaNodeDto[] {
		let areaTreeClone = [...this.areaTree]
		if (this.areasToOmit && this.areasToOmit.length) {
			areaTreeClone.forEach(area => {
				if (area.children && area.children.length) {
					area.children = this.filterOmittedAreas(area.children)
				}
			})
		}
		return areaTreeClone
	}

	private filterOmittedAreas (areas:AreaNodeDto[]):AreaNodeDto[] {
		const filteredAreas = areas.filter(child => {
			if (child.children && child.children.length) {
				child.children = this.filterOmittedAreas(child.children)
			}
			return !this.areasToOmit.includes(child.id)
		})
		return filteredAreas
	}

	@Watch("value")
	private async onValueChanged (): Promise<void> {
		if (!this.showState) {
			return
		}
		// This is a work around to force VueTreeselect to re-render
		this.rerenderKey += 1
		await this.loadAreaTree()
	}

	private async loadOptions (
		{ action, parentNode, callback, searchQuery }:
		{ action: string, parentNode: AreaNodeDto, callback: Function, searchQuery: string }
	): Promise<void> {
		if (action === LOAD_CHILDREN_OPTIONS) {
			let groupTreeNode = await api.getAreaTreeNode(
				parentNode.id
			)
			parentNode.children = groupTreeNode.children
			callback()
		}
		if (action === ASYNC_SEARCH) {
			// If the SearchQuery does not meet the minimum requirements we need to hide the retry button when showing the hint text
			this.hideRetryButton = searchQuery.length > 0 && searchQuery.length < this.minSearchQueryLength

			if (searchQuery.length >= this.minSearchQueryLength) {
				this.isSearching = true
				this.isSearchComplete = false
				let areaNodeSearchResult = this.setLeafNodes(await api.searchAreaTree(
					searchQuery
				))

				if (!areaNodeSearchResult) {
					callback(null, [])
				} else {
					callback(null, [...areaNodeSearchResult])
				}

				// Ensure the retry button is shown again in case an Error occurs
				this.hideRetryButton = false
				this.isSearchComplete = true
			} else {
				callback(new Error(`Search must be at least ${this.minSearchQueryLength} characters`), [])
			}
		}
	}

	@Emit("is-open")
	private close (): boolean {
		this.isSearching = false
		return false
	}

	@Emit("is-open")
	private open (): boolean {
		return true
	}

	private searchValueChanged (searchQuery: string): void {
		this.isSearching = searchQuery.length >= this.minSearchQueryLength
	}

	private onInputChanged (node: AreaNodeDto[]): void {
		if (!node) {
			if (this.clearable) {
				this.$emit("input", null)
			}
			return
		}
		if (this.fullPathValue && node.length > 0) {
			let nodesToRemove: string[] = []
			let selectedNode = node[0]
			let setNodesToRemove = (n: AreaNodeDto) => {
				if (n.children && n.children.length > 0) {
					n.children.forEach((cn: AreaNodeDto) => {
						nodesToRemove.push(cn.id)
						setNodesToRemove(cn)
					})
				}
			}
			setNodesToRemove(selectedNode)
			let result = node
				.filter((n) => !nodesToRemove.includes(n.id))
				.reverse()
			this.$emit("input", result)
			this.$refs.treeSelect.closeMenu()
		}
	}

	private selected (node: AreaNodeDto): void {
		if (this.fullPathValue === false) {
			if (this.multiple) {
				let result: string[] = []
				if (this.value && typeof (this.value) === "string") {
					result = [...this.value]
				}
				result.push(this.reduce(node))
				this.$emit("input", result)
			} else {
				this.$emit("input", this.reduce(node))
			}
			this.clearInputWhenSelected()
		}
	}

	private deselect (node: AreaNodeDto): void {
		if (this.multiple) {
			let result = [...this.value]
			let nodeToRemove = result.findIndex((n) => n === node.id)
			if (nodeToRemove !== -1) {
				result.splice(nodeToRemove, 1)
			}
			this.$emit("input", result)
		} else {
			this.$emit("input", this.reduce(node))
		}
	}

	private setLeafNodes (areaNodes: AreaNodeDto[]): AreaNodeDto[] {
		if (!areaNodes) return []
		areaNodes.forEach((areaNode) => {
			if (areaNode.children && areaNode.children.length > 0) {
				areaNode.children = this.setLeafNodes(areaNode.children)
			} else {
				areaNode.children = undefined
			}
		})
		return areaNodes
	}

	private get valueFormat (): string {
		if (this.fullPathValue) {
			return "node"
		}
		if (this.multiple) {
			return "id"
		}
		return typeof this.value === "string" ? "id" : "node"
	}

	private clearInputWhenSelected (): void {
		if (this.clearInputOnSelect) {
			this.$refs.treeSelect.clear()
		}
	}

	private checkIfRowIsEllipsised (id: string): boolean {
		// Get the row we want to check
		const row: HTMLElement = this.$refs[`${this.rowPrefix}${id}`]

		// If we have the details
		if (row && row.scrollWidth && row.clientWidth) {
			// check if the width of the text exceeds the visible width of the row
			return row.scrollWidth > row.clientWidth
		}

		// If we didn't find the row return false
		return false
	}
}
